// src/pages/Shop.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { Filter, Grid, List, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Product, Category } from '../types';
import { ProductCard } from '../components/ProductCard';
import { useLocation, useNavigate } from 'react-router-dom';
import { categoriesApi } from '../utils/api';

export const Shop = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 20000]);
  const [sortBy, setSortBy] = useState<string>('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { state, dispatch } = useApp();
  const itemsPerPage = 20;
  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);
  const categoryFromQuery = query.get('category');
  const subcategoryFromQuery = query.get('subcategory');

  // Fetch categories from backend using our API client
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await categoriesApi.getAll();
        if (response.data) {
          setCategories(response.data);
        }
      } catch (err) {
        setError('Failed to load categories. Please try again later.');
        console.error('Error fetching categories:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Update selectedCategory and selectedSubcategory based on the query parameters
  useEffect(() => {
    if (categoryFromQuery) {
      setSelectedCategory(categoryFromQuery);
    }
    if (subcategoryFromQuery) {
      setSelectedSubcategory(subcategoryFromQuery);
    }
  }, [categoryFromQuery, subcategoryFromQuery]);

  // Add this useEffect to handle scroll reset when component mounts or category changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [categoryFromQuery]); // Trigger when category changes

  // Filter the products in memory
  const filteredProducts = useMemo(() => {
    let filtered = [...state.products];

    // Search by name/description
    if (state.searchQuery) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(state.searchQuery.toLowerCase()) ||
        product.description.toLowerCase().includes(state.searchQuery.toLowerCase())
      );
    }

    // Category filter
    if (selectedCategory) {
      filtered = filtered.filter(product => product.category_id.toString() === selectedCategory);
    }

    // Subcategory filter
    if (selectedSubcategory) {
      filtered = filtered.filter(product => product.subcategory_id.toString() === selectedSubcategory);
    }

    // Price range filter
    filtered = filtered.filter(
      product => product.price >= priceRange[0] && product.price <= priceRange[1]
    );

    // Sort
    switch (sortBy) {
      case 'price-asc':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'name-asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      default:
        break;
    }

    return filtered;
  }, [state.products, state.searchQuery, selectedCategory, selectedSubcategory, priceRange, sortBy]);

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const currentProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setSelectedSubcategory(''); // Clear subcategory when category changes
    // Update URL without subcategory
    const newQuery = new URLSearchParams(location.search);
    if (categoryId) {
      newQuery.set('category', categoryId);
    } else {
      newQuery.delete('category');
    }
    newQuery.delete('subcategory');
    navigate(`/shop?${newQuery.toString()}`);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Mobile Filters Button */}
      <div className="md:hidden mb-4">
        <button
          onClick={() => setShowMobileFilters(true)}
          className="w-full bg-pink-500 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2"
        >
          <Filter className="h-5 w-5" />
          Show Filters
        </button>
      </div>

      {/* Mobile Filters Drawer */}
      {showMobileFilters && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 md:hidden">
          <div className="fixed inset-y-0 right-0 w-full max-w-xs bg-white shadow-xl p-6 overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold">Filters</h3>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="p-2 rounded-full hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Categories */}
            <div className="mb-6">
              <h4 className="font-medium mb-2">Categories</h4>
              <div className="space-y-2">
                <button
                  className={`w-full text-left px-3 py-2 rounded-md ${
                    !selectedCategory ? 'bg-pink-100 text-pink-600' : 'hover:bg-gray-100'
                  }`}
                  onClick={() => {
                    handleCategorySelect('');
                    setShowMobileFilters(false);
                  }}
                >
                  All Categories
                </button>
                {isLoading ? (
                  <div className="text-gray-500">Loading categories...</div>
                ) : error ? (
                  <div className="text-red-500">{error}</div>
                ) : (
                  categories.map(category => (
                    <button
                      key={category.id}
                      className={`w-full text-left px-3 py-2 rounded-md ${
                        selectedCategory === category.id.toString()
                          ? 'bg-pink-100 text-pink-600'
                          : 'hover:bg-gray-100'
                      }`}
                      onClick={() => {
                        handleCategorySelect(category.id.toString());
                        setShowMobileFilters(false);
                      }}
                    >
                      {category.name}
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Price Range */}
            <div className="mb-6">
              <h4 className="font-medium mb-2">Price Range</h4>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-600">Min Price</label>
                  <input
                    type="range"
                    min="0"
                    max="20000"
                    value={priceRange[0]}
                    onChange={(e) =>
                      setPriceRange([parseInt(e.target.value), priceRange[1]])
                    }
                    className="w-full"
                  />
                  <span className="text-sm text-gray-600">
                    KES {priceRange[0].toLocaleString()}
                  </span>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Max Price</label>
                  <input
                    type="range"
                    min="0"
                    max="20000"
                    value={priceRange[1]}
                    onChange={(e) =>
                      setPriceRange([priceRange[0], parseInt(e.target.value)])
                    }
                    className="w-full"
                  />
                  <span className="text-sm text-gray-600">
                    KES {priceRange[1].toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowMobileFilters(false)}
              className="w-full bg-pink-500 text-white py-2 rounded-lg mt-4"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-8">
        {/* Desktop Filters Sidebar */}
        <div className="hidden md:block w-64">
          <div className="sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Filters
              </h3>

              {/* Categories */}
              <div className="mb-6">
                <h4 className="font-medium mb-2">Categories</h4>
                <div className="space-y-2">
                  <button
                    className={`w-full text-left px-2 py-1 rounded ${
                      !selectedCategory ? 'bg-pink-100 text-pink-600' : 'hover:bg-gray-100'
                    }`}
                    onClick={() => handleCategorySelect('')}
                  >
                    All Categories
                  </button>
                  {isLoading ? (
                    <div className="text-gray-500">Loading categories...</div>
                  ) : error ? (
                    <div className="text-red-500">{error}</div>
                  ) : (
                    categories.map(category => (
                      <button
                        key={category.id}
                        className={`w-full text-left px-2 py-1 rounded ${
                          selectedCategory === category.id.toString()
                            ? 'bg-pink-100 text-pink-600'
                            : 'hover:bg-gray-100'
                        }`}
                        onClick={() => handleCategorySelect(category.id.toString())}
                      >
                        {category.name}
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Price Range */}
              <div className="mb-6">
                <h4 className="font-medium mb-2">Price Range</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min="0"
                      max="20000"
                      value={priceRange[0]}
                      onChange={(e) =>
                        setPriceRange([parseInt(e.target.value), priceRange[1]])
                      }
                      className="w-full"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min="0"
                      max="20000"
                      value={priceRange[1]}
                      onChange={(e) =>
                        setPriceRange([priceRange[0], parseInt(e.target.value)])
                      }
                      className="w-full"
                    />
                  </div>
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>KES {priceRange[0].toLocaleString()}</span>
                    <span>KES {priceRange[1].toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Products Section */}
        <div className="flex-1">
          {/* Toolbar */}
          <div className="bg-white p-4 rounded-lg shadow-md mb-6 flex flex-wrap gap-4 items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setView('grid')}
                className={`p-2 rounded ${
                  view === 'grid' ? 'bg-pink-100 text-pink-600' : 'hover:bg-gray-100'
                }`}
              >
                <Grid className="h-5 w-5" />
              </button>
              <button
                onClick={() => setView('list')}
                className={`p-2 rounded ${
                  view === 'list' ? 'bg-pink-100 text-pink-600' : 'hover:bg-gray-100'
                }`}
              >
                <List className="h-5 w-5" />
              </button>
              <span className="text-gray-600">
                Showing {currentProducts.length} of {filteredProducts.length} products
              </span>
            </div>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500"
            >
              <option value="">Sort by</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="name-asc">Name: A to Z</option>
              <option value="name-desc">Name: Z to A</option>
            </select>
          </div>

          {/* Products Grid or List */}
          <div
            className={
              view === 'grid'
                ? 'grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6'
                : 'space-y-4'
            }
          >
            {currentProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                variant={view === 'list' ? 'featured' : 'default'}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-8 flex justify-center items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-full hover:bg-pink-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`w-10 h-10 rounded-full ${
                    currentPage === page
                      ? 'bg-pink-500 text-white'
                      : 'hover:bg-pink-100'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-full hover:bg-pink-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
